import FontAwesome from '@expo/vector-icons/FontAwesome';
import { useFonts } from 'expo-font';
import * as SplashScreen from 'expo-splash-screen';
import { useEffect } from 'react';
import 'react-native-reanimated';

import React from 'react';
import { GestureHandlerRootView } from 'react-native-gesture-handler';
import { AuthProvider } from '@/services/auth/AuthProvider';
import { ApolloProvider } from '@/services/apollo/ApolloProvider';
import { Slot, useNavigationContainerRef } from 'expo-router';
import { PaperProvider } from 'react-native-paper';
import {
  SafeAreaProvider,
  initialWindowMetrics,
  useSafeAreaInsets,
} from 'react-native-safe-area-context';
import { defaultTheme } from '@/shared/styles/theme';
import SentryService from '@/services/sentry/Sentry.service';
import { ZoomProvider } from '@/services/zoom/ZoomProvider';
import { TwilioProvider } from '@/services/twilio/TwilioProvider';
import { SendbirdProvider } from '@/services/sendBird/SendbirdProvider';
import { StatusBar, View } from 'react-native';
import { StripeProvider } from '@/services/stripe/StripeProvider';
import { KeyboardProvider } from 'react-native-keyboard-controller';
import Portals from '@/shared/components/portals/Portals';
import { useAuthRouting } from '@/shared/hooks/useAuthRouting';
import { FullScreenLoader } from '@/shared/components/FullScreenLoader';
import { useLoadEnvironment } from '@/shared/hooks/useLoadEnvironment';
import DrawerContent from '@/shared/components/navigation/DrawerContent';
import { Drawer } from 'expo-router/drawer';
import { IS_WEB } from '@/shared/constants/utility';
import { useRouteTheme } from '@/shared/hooks/useRouteTheme';
import { useNotifications } from '@/shared/hooks/useNotifications';
import { useAppVersionCheck } from '@/shared/hooks/useAppVersionCheck';
import { en, registerTranslation } from 'react-native-paper-dates';

export {
  // Catch any errors thrown by the Layout component.
  ErrorBoundary,
} from 'expo-router';

export const unstable_settings = {
  // Ensure that reloading on `/modal` keeps a back button present.
  initialRouteName: '(main)',
};

// Prevent the splash screen from auto-hiding before asset loading is complete.
SplashScreen.preventAutoHideAsync();

export default function AppRoot() {
  const navigationRef = useNavigationContainerRef();

  const [fontsLoaded, error] = useFonts({
    SpaceMono: require('../assets/fonts/SpaceMono-Regular.ttf'),
    Lora: require('@/assets/fonts/Lora-VariableFont_wght.ttf'),
    LoraItalic: require('@/assets/fonts/Lora-Italic-VariableFont_wght.ttf'),
    ...FontAwesome.font,
  });

  registerTranslation('en', en);

  // Expo Router uses Error Boundaries to catch errors in the navigation tree.
  useEffect(() => {
    if (error) throw error;
  }, [error]);

  useEffect(() => {
    if (fontsLoaded) {
      SplashScreen.hideAsync();
    }
  }, [fontsLoaded]);

  useEffect(() => {
    SentryService.registerNavigationContainer(navigationRef);
  }, [navigationRef]);

  const isEnvLoaded = useLoadEnvironment();

  useNotifications();

  if (!fontsLoaded || !isEnvLoaded) {
    return null;
  }

  return (
    <SafeAreaProvider initialMetrics={initialWindowMetrics}>
      <AuthProvider>
        <ApolloProvider>
          <SendbirdProvider>
            <ZoomProvider>
              <TwilioProvider>
                <StripeProvider>
                  <GestureHandlerRootView>
                    <KeyboardProvider>
                      <PaperProvider theme={defaultTheme}>
                        <RootLayout />
                      </PaperProvider>
                    </KeyboardProvider>
                  </GestureHandlerRootView>
                </StripeProvider>
              </TwilioProvider>
            </ZoomProvider>
          </SendbirdProvider>
        </ApolloProvider>
      </AuthProvider>
    </SafeAreaProvider>
  );

  function RootLayout() {
    const isRoutingReady = useAuthRouting();
    const theme = useRouteTheme();
    useAppVersionCheck();
    const insets = useSafeAreaInsets();

    // Web doesn't need safe area handling
    if (IS_WEB) {
      return (
        <View
          style={{ flex: 1, backgroundColor: theme.bottomSafeAreaBackground }}
        >
          <Slot initialRouteName="(main)" />
          <Portals />
          {!isRoutingReady && <FullScreenLoader overlay />}
        </View>
      );
    }

    // Mobile with manual inset handling
    return (
      <View style={{ flex: 1 }}>
        <View
          style={{
            height: insets.top,
            backgroundColor: theme.topSafeAreaBackground,
          }}
        />

        <View
          style={{
            flex: 1,
            paddingBottom: insets.bottom,
            paddingLeft: insets.left,
            paddingRight: insets.right,
            backgroundColor: theme.bottomSafeAreaBackground,
          }}
        >
          <StatusBar barStyle={theme.statusBarStyle} />
          <DrawerLayout />
          <Portals />
          {!isRoutingReady && <FullScreenLoader overlay />}
        </View>
      </View>
    );
  }

  function DrawerLayout() {
    return (
      <Drawer
        drawerContent={DrawerContent}
        screenOptions={{
          headerShown: false,
          drawerPosition: 'right',
          drawerType: 'front',
          drawerStyle: {
            width: '85%',
          },
          swipeEdgeWidth: 0,
        }}
      >
        <Drawer.Screen name="(main)" />
      </Drawer>
    );
  }
}
